import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import CssBaseline from "@material-ui/core/CssBaseline";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import { CodeLookupResultBody } from "../Negocio/CodeLookupDialog";
import { publicFileUrl } from "../Negocio/helpers";

const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const publicApi = axios.create({ baseURL: backend, withCredentials: false });

const groupMails = (mails) => {
  const products = [];
  const byId = new Map();
  (mails || []).forEach((mail) => {
    const pid = String(mail.productId);
    if (!byId.has(pid)) {
      const group = {
        productId: mail.productId,
        productName: mail.productName || "Producto",
        productImage: mail.productImage || "",
        emails: [],
      };
      byId.set(pid, group);
      products.push(group);
    }
    const group = byId.get(pid);
    const email = String(mail.email || "").trim();
    const key = email.toLowerCase();
    if (!email || group.emails.some((item) => item.email.toLowerCase() === key)) return;
    group.emails.push({ email, subscriptionId: mail.subscriptionId });
  });
  products.sort((a, b) => a.productName.localeCompare(b.productName, "es", { sensitivity: "base" }));
  products.forEach((group) => {
    group.emails.sort((a, b) => a.email.localeCompare(b.email, "es", { sensitivity: "base" }));
  });
  return products;
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    overflowY: "auto",
    background:
      "radial-gradient(1200px 500px at 20% 10%, rgba(59,130,246,0.18), transparent 60%)," +
      "radial-gradient(900px 400px at 80% 30%, rgba(16,185,129,0.12), transparent 55%)," +
      "linear-gradient(180deg, #f7f9ff 0%, #edf2ff 100%)",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: 0,
    },
  },
  paper: {
    width: "100%",
    maxWidth: 480,
    borderRadius: 18,
    padding: theme.spacing(4),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 18px 60px rgba(15, 23, 42, 0.10)",
    border: "1px solid rgba(15,23,42,0.06)",
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(10px)",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      minHeight: "100dvh",
      borderRadius: 0,
      boxShadow: "none",
      border: "none",
      margin: 0,
      padding: theme.spacing(2.5),
      paddingTop: "max(16px, env(safe-area-inset-top, 0px))",
      paddingBottom: "max(24px, env(safe-area-inset-bottom, 0px))",
    },
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  logo: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#fff",
    objectFit: "cover",
  },
  companyName: {
    fontWeight: 900,
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.2,
  },
  title: {
    marginBottom: theme.spacing(1),
    fontSize: 28,
    fontWeight: 900,
    color: "#0f172a",
    letterSpacing: -0.5,
  },
  subtitle: {
    color: "#64748b",
    marginBottom: theme.spacing(2.5),
    fontSize: 14,
  },
  field: {
    marginBottom: 14,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fff",
      minHeight: 56,
    },
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  mark: {
    width: 28,
    height: 28,
    borderRadius: 8,
    objectFit: "cover",
    background: "#e2e8f0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 10,
    color: "#1e3a8a",
    flexShrink: 0,
  },
  optionText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  optionName: {
    display: "block",
    fontWeight: 800,
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.2,
  },
  optionMeta: {
    display: "block",
    color: "#64748b",
    fontSize: 12,
  },
  hint: {
    color: "#64748b",
    fontSize: 12,
    margin: "-6px 2px 14px",
  },
  submit: {
    marginTop: 8,
    height: 48,
    borderRadius: 12,
    fontWeight: 900,
    background: "linear-gradient(45deg, #2563eb 30%, #1d4ed8 90%)",
    color: "#fff",
    textTransform: "none",
  },
  back: {
    marginTop: 8,
    textTransform: "none",
    fontWeight: 700,
  },
  error: {
    color: "#c62828",
    fontSize: 14,
    marginBottom: 12,
  },
  footer: {
    marginTop: 24,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
  },
}));

function ProductMark({ group, className }) {
  if (group?.productImage) {
    return <img src={publicFileUrl(group.productImage)} alt="" className={className} />;
  }
  return <span className={className}>{(group?.productName || "?").slice(0, 2).toUpperCase()}</span>;
}

export default function PublicCodes() {
  const classes = useStyles();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [productId, setProductId] = useState("");
  const [emailKey, setEmailKey] = useState("");
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [logo] = useState(() => `${backend}/public/logotipos/login.png?r=${Date.now()}`);

  const products = useMemo(() => groupMails(session?.mails), [session]);
  const product = products.find((item) => String(item.productId) === String(productId)) || null;
  const emails = product?.emails || [];
  const chosen = emails.find((item) => item.email.toLowerCase() === String(emailKey).toLowerCase()) || null;

  useEffect(() => {
    document.title = "Consulta de códigos · WATOOLX";
    let cancelled = false;
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/negocio/codes/${token}`);
        if (!cancelled) setSession(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err?.response?.data?.message || "Este enlace no es válido.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!products.length) return;
    if (!productId || !products.some((item) => String(item.productId) === String(productId))) {
      const first = products[0];
      setProductId(String(first.productId));
      setEmailKey(first.emails.length === 1 ? first.emails[0].email : "");
    }
  }, [products, productId]);

  const pickProduct = (id) => {
    const next = products.find((item) => String(item.productId) === String(id));
    setProductId(String(id));
    setEmailKey(next?.emails.length === 1 ? next.emails[0].email : "");
    setSelected(null);
    setResult(null);
    setError("");
  };

  const search = async () => {
    if (!chosen) return;
    setSelected(chosen);
    setSearching(true);
    setResult(null);
    setError("");
    try {
      const { data } = await publicApi.post(`/public/negocio/codes/${token}/lookup`, {
        subscriptionId: chosen.subscriptionId,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "No se pudo consultar el código.");
    } finally {
      setSearching(false);
    }
  };

  const showForm = !searching && !result;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Paper className={classes.paper} elevation={0}>
        <div className={classes.brandRow}>
          <img src={logo} alt="WATOOLX" className={classes.logo} />
          <div className={classes.companyName}>WATOOLX</div>
        </div>
        {showForm ? (
          <>
            <Typography className={classes.title}>Consulta de códigos</Typography>
            {session?.name ? (
              <Typography className={classes.subtitle}>Hola {session.name}. Elige el producto y el correo.</Typography>
            ) : (
              <Typography className={classes.subtitle}>Códigos de verificación de tus plataformas.</Typography>
            )}
          </>
        ) : null}
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : error && !session ? (
          <div className={classes.error}>{error}</div>
        ) : (
          <>
            {error ? <div className={classes.error}>{error}</div> : null}
            {!products.length ? (
              <div className={classes.subtitle}>No tienes correos asignados para consultar.</div>
            ) : null}
            {showForm && products.length ? (
              <>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="Producto"
                  value={productId}
                  onChange={(e) => pickProduct(e.target.value)}
                  className={classes.field}
                  SelectProps={{
                    renderValue: () =>
                      product ? (
                        <span className={classes.option}>
                          <ProductMark group={product} className={classes.mark} />
                          <span className={classes.optionText}>
                            <span className={classes.optionName}>{product.productName}</span>
                          </span>
                        </span>
                      ) : (
                        "Selecciona un producto"
                      ),
                  }}
                >
                  {products.map((item) => (
                    <MenuItem key={item.productId} value={String(item.productId)}>
                      <span className={classes.option}>
                        <ProductMark group={item} className={classes.mark} />
                        <span className={classes.optionText}>
                          <span className={classes.optionName}>{item.productName}</span>
                          <span className={classes.optionMeta}>
                            {item.emails.length === 1 ? "1 correo" : `${item.emails.length} correos`}
                          </span>
                        </span>
                      </span>
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="Correo"
                  value={emailKey}
                  onChange={(e) => {
                    setEmailKey(e.target.value);
                    setSelected(null);
                    setResult(null);
                    setError("");
                  }}
                  disabled={!product}
                  className={classes.field}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => value || "Selecciona un correo",
                  }}
                >
                  {emails.length ? (
                    emails.map((item) => (
                      <MenuItem key={item.subscriptionId} value={item.email}>
                        {item.email}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      Sin correos en este producto
                    </MenuItem>
                  )}
                </TextField>
                <div className={classes.hint}>
                  {product
                    ? `${product.productName} · ${emails.length === 1 ? "1 correo asignado" : `${emails.length} correos asignados`}`
                    : "Primero elige el producto."}
                </div>
                <Button
                  fullWidth
                  className={classes.submit}
                  disabled={!chosen}
                  onClick={search}
                >
                  Consultar código
                </Button>
              </>
            ) : null}
            <CodeLookupResultBody
              result={result}
              loading={searching}
              publicMode
              onReset={() => {
                setSelected(null);
                setResult(null);
                setError("");
              }}
            />
          </>
        )}
        <div className={classes.footer}>© {new Date().getFullYear()} watoolx.com</div>
      </Paper>
    </div>
  );
}
